import {EventEmitter, Injectable, Renderer2, RendererFactory2, OnDestroy} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as pdfjsLib from 'pdfjs-dist';
import {PDFDocumentProxy} from 'pdfjs-dist';
import {cloneDeep} from 'lodash-es';
import {Subject, fromEvent, tap, Subscription} from 'rxjs';
import moment from 'moment-timezone';
import * as statuses from '../../modules/sales-terminal/event-search/event-search.constants';
import {PDFPageProxy} from 'pdfjs-dist/types/src/display/api';
import {EventStateEnum, Period, SortOrderEnum} from '../../generated/sales-terminal-client';
import {ViewService} from '../view/view.service';

export interface FileData {
  extension: string;
  mime: string;
  filename: string;
  firefox: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UtilService implements OnDestroy {
  private resizeRef: any;
  private resizeInterval: any;
  private onResized = fromEvent(window, 'resize').pipe(
    tap($event => {
      // Clear previous timeout
      clearTimeout(this.resizeRef);
      this.resizeRef = setTimeout(() => {
        // Fire on resize end
        this.windowResizeHelper.next(new Date());
        // Destroy interval
        clearInterval(this.resizeInterval);
      }, 100);
      if (!this.resizeInterval) {
        this.resizeInterval = setInterval(() => {
          // Fire only every 50ms to not spam on CPU
          this.windowResizeHelper.next(new Date());
        }, 50);
      }
    })
  );
  private onResizedSub: Subscription;

  public static STORAGE_CONTENT_SCROLL = 'util.contentScroll';
  public static KEY_ENTER = 'Enter';
  public static KEY_UP_ARROW = 'ArrowUp';
  public static KEY_RIGHT_ARROW = 'ArrowRight';
  public static KEY_DOWN_ARROW = 'ArrowDown';
  public static KEY_LEFT_ARROW = 'ArrowLeft';
  public static KEY_SPACE = ' ';
  public static CODE_A = 'KeyA';
  public static CODE_Z = 'KeyZ';
  public static CODE_0 = 'Digit0';
  public static CODE_9 = 'Digit9';

  public tokenExpired: EventEmitter<any> = new EventEmitter();
  public tokenRefresh: EventEmitter<any> = new EventEmitter();
  public userLoggedIn: EventEmitter<any> = new EventEmitter();
  public userLoggedInNoRedirect: boolean;
  public userLoggedOut: EventEmitter<any> = new EventEmitter();
  public userRoleSwitched: EventEmitter<any> = new EventEmitter();
  public userPermissionsFetched: EventEmitter<any> = new EventEmitter();
  public fiscalOperationFinished: EventEmitter<any> = new EventEmitter();
  public fiscalOperationFailed: EventEmitter<any> = new EventEmitter();
  public bookingQueried: Subject<any> = new Subject<{ id: number; title: string }>();
  public contentScrolledTop: number = 0;
  public contentScrolled: EventEmitter<any> = new EventEmitter();
  public overflowUnset: boolean;
  public virtualKeyboardSupported: boolean;
  public virtualKeyboardOpened: boolean;
  public virtualKeyboardOpenedChange: EventEmitter<any> = new EventEmitter();
  public virtualKeyboardHeight: number = 0;
  private renderer: Renderer2;
  private iFrameRef: any;
  private queuedAction: any[] = undefined;
  private queuedActionRef: any;
  private queuedActionPages: number = 0;
  private queuedActionPagesRendered: number = 0;
  public translationsLoaded: boolean = false;
  public translationsLoadedChange: EventEmitter<any> = new EventEmitter();
  public hasHeader: boolean;
  public sidenavWidth: number = 0;
  public fixedSnack: EventEmitter<any> = new EventEmitter();
  public fixedSnackHeight: number = 0;
  public snackDismissed: EventEmitter<any> = new EventEmitter();
  public windowResizeHelper: Subject<any> = new Subject<any>();

  constructor(private rendererFactory: RendererFactory2,
              public translateService: TranslateService) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.translationsLoadedChange.subscribe((value) => {
      this.translationsLoaded = value;
    })
    this.virtualKeyboardOpenedChange.subscribe((value) => {
      this.virtualKeyboardOpened = !!value;
      this.virtualKeyboardHeight = value;
    });
    this.onResizedSub = this.onResized.subscribe();
  }

  ngOnDestroy(): void {
    this.onResizedSub.unsubscribe();
  }

  static getBrowser(check: string): boolean {
    const agent = window.navigator.userAgent.toLowerCase();
    let match: string;
    if (agent.indexOf('firefox') > -1) {
      match = 'ff';
    } else if (agent.indexOf('msie') > -1 || !!(document as any).documentMode) {
      match = 'ie';
    } else if (agent.indexOf('chrome') > -1) {
      match = 'chrome';
    } else if (agent.indexOf('safari') > -1) {
      match = 'safari';
    }
    return match === check;
  }

  static isHEICImage(file: File): boolean {
    if (this.isWindowsOs() && !file.type) {
      return file.name && /.heic$/.test(file.name.toLowerCase());
    }
    return /image\/heic/.test(file.type);
  }

  static isWindowsOs() {
    return window.navigator.userAgent.toLowerCase().indexOf('windows') > -1;
  }

  static parseJSON(value: string): any {
    return !!value && typeof value === 'object' && value || JSON.parse(value);
  }

  static getFileData(name: string, format: string): FileData {
    const data: FileData = {
      extension: format,
      mime: 'application/plp',
      filename: name || 'prindi_pilet',
      firefox: UtilService.getBrowser('ff')
    };
    if (format === 'pdf') {
      data.extension = format;
      data.mime = 'application/pdf';
    }
    if (format === 'html') {
      data.extension = format;
      data.mime = 'text/html';
    }
    if (format === 'json') {
      data.extension = 'plp';
      data.mime = 'application/octet-stream';
    }
    data.filename += '.' + data.extension;
    return data;
  }

  public previewOrPrintFile(file: Blob,
                            styles: string = null,
                            id: string = null,
                            type: string = 'html',
                            fileName: string = '',
                            printedCountBy: string = 'all') {
    this.disposeIframe();
    const fileUrl = URL.createObjectURL(file);
    this.iFrameRef = this.renderer.createElement('div');
    const frame = this.renderer.createElement('iframe');
    const buttons = this.renderer.createElement('div');
    if (!id) {
      id = 'id_' + Math.random() * 1000000000;
    }
    this.renderer.setAttribute(frame, 'id', id);
    if (fileName) {
      let pages = '';
      if (this.queuedActionRef) {
        pages = ' (' + this.queuedActionRef.innerHTML.split(': ')[1].replace('/', '-') + ')';
      }
      this.renderer.setAttribute(frame, 'title', fileName + pages);
    }

    if (type === 'pdf') {
      const html = '<html>' +
        '<head>' +
        '<style media="all" type="text/css">' +
        '@page {size: A4 portrait; margin: 0; padding: 0;}' +
        'body {padding: 0; margin: 0;}' +
        '</style>' +
        (fileName ? '<title>' + fileName + '</title>' : '') +
        '</head>' +
        '<body>' +
        '</body>' +
        '</html>';
      const blob = new Blob([html], {type: 'text/html'});
      this.renderer.setAttribute(frame, 'src', URL.createObjectURL(blob));
    } else {
      this.renderer.setAttribute(frame, 'src', fileUrl);
    }

    if (!UtilService.getBrowser('ff')) {
      this.renderer.setStyle(this.iFrameRef, 'display', 'none');
    } else {
      this.renderer.addClass(this.iFrameRef, 'printing-preview-frame');
      this.renderer.addClass(frame, 'frame-wrapper');
      this.renderer.addClass(buttons, 'buttons');

      const closeBtn = this.renderer.createElement('button');
      this.renderer.addClass(closeBtn, 'mat-raised-button');
      this.renderer.addClass(closeBtn, 'mat-button-md');
      this.renderer.addClass(closeBtn, 'margin-left');
      this.renderer.addClass(closeBtn, 'margin-right');
      const closeBtnWrapper = this.renderer.createElement('span');
      this.renderer.addClass(closeBtnWrapper, 'mat-button-wrapper');
      const closeText = this.renderer.createText(this.translateService.instant('general.api_general_close'));
      this.renderer.appendChild(closeBtnWrapper, closeText);
      this.renderer.appendChild(closeBtn, closeBtnWrapper);
      this.renderer.listen(closeBtn, 'click', () => {
        if (this.queuedAction === undefined) {
          this.disposeIframe();
        }
        this.executeAction();
      });
      this.renderer.appendChild(buttons, closeBtn);

      const printBtn = this.renderer.createElement('button');
      this.renderer.addClass(printBtn, 'mat-raised-button');
      this.renderer.addClass(printBtn, 'mat-button-md');
      this.renderer.addClass(printBtn, 'mat-primary');
      this.renderer.addClass(printBtn, 'margin-left');
      this.renderer.addClass(printBtn, 'margin-right');
      const printBtnWrapper = this.renderer.createElement('span');
      this.renderer.addClass(printBtnWrapper, 'mat-button-wrapper');
      const printText = this.renderer.createText(this.translateService.instant('general.api_general_print'));
      this.renderer.appendChild(printBtnWrapper, printText);
      this.renderer.appendChild(printBtn, printBtnWrapper);
      this.renderer.listen(printBtn, 'click', () => {
        frame.contentWindow.print();
        if (this.queuedAction === undefined) {
          const disposeEvent = () => {
            this.disposeIframe();
            setTimeout(() => {
              window.removeEventListener('mousemove', disposeEvent);
            }, 100);
          };
          window.addEventListener('mousemove', disposeEvent);
        }
        this.executeAction();
      });
      this.renderer.appendChild(buttons, printBtn);
    }

    this.renderer.appendChild(this.iFrameRef, frame);
    this.renderer.appendChild(this.iFrameRef, buttons);
    this.renderer.appendChild(document.body, this.iFrameRef);

    setTimeout(() => {
      if (fileName && UtilService.getBrowser('ff')) {
        frame.contentWindow.document.title = fileName;
      }
      if (styles) {
        const head = frame.contentWindow.document.getElementsByTagName('head')[0];
        const style = this.renderer.createElement('style');
        style.type = 'text/css';
        style.appendChild(document.createTextNode(styles));
        head.appendChild(style);
      }
      if (type === 'pdf') {
        this.printPdf(frame, file, fileUrl, fileName, printedCountBy);
      } else {
        this.setFilenameAndPrint(frame, fileName);
      }
    }, 1000);

    return {ref: this.iFrameRef, dispose: this.disposeIframe};
  }

  public printPdf(frame: HTMLIFrameElement, file: Blob, fileUrl: string, fileName: string, printedCountBy: string, continueFromPage = 1) {
    const body = frame.contentWindow.document.getElementsByTagName('body')[0];
    const options = {scale: 5};

    body.innerHTML = '';

    const renderPage = (page: PDFPageProxy) => {
      const viewport = page.getViewport({
        scale: options.scale
      });
      const wrapper = document.createElement('div');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const renderContext = {
        canvasContext: ctx,
        viewport: viewport,
        intent: 'print'
      };

      canvas.height = viewport.height - 25;
      canvas.width = viewport.width;
      canvas.style.width = '100%';
      canvas.style.height = 'auto';
      wrapper.style.width = '100%';
      wrapper.style.height = '100%';
      const num = page._pageIndex + 1;
      const maxPage = printedCountBy === 'all' ? this.queuedActionPages : (continueFromPage + parseInt(printedCountBy, 0) - 1);
      if (printedCountBy === 'all' || (continueFromPage <= num && maxPage >= num)) {
        page.render(renderContext).promise.then(() => {
          wrapper.appendChild(canvas);
          body.appendChild(wrapper);
          this.queuedActionPagesRendered++;
          if (this.queuedActionPagesRendered === this.queuedActionPages ||
            (printedCountBy !== 'all' && this.queuedActionPagesRendered === (maxPage <= this.queuedActionPages ? printedCountBy :
              (this.queuedActionPages - continueFromPage + 1)) && !UtilService.getBrowser('ff'))) {
            setTimeout(() => {
              this.setFilenameAndPrint(frame, fileName, true);
            }, 500);
          }
        });
      }
    };

    const renderPages = (pdfDoc: PDFDocumentProxy) => {
      this.queuedActionPagesRendered = 0;
      this.queuedActionPages = pdfDoc.numPages;
      for (let num = 1; num <= pdfDoc.numPages; num++) {
        pdfDoc.getPage(num).then(renderPage);
        if (num === pdfDoc.numPages) {
          this.showPrintingQueue(continueFromPage, printedCountBy, pdfDoc.numPages);
          this.queuedAction = undefined;
          const nextQueue = continueFromPage + parseInt(printedCountBy, 0);
          if (printedCountBy !== 'all' && nextQueue <= pdfDoc.numPages) {
            this.queuedAction = [frame, fileUrl, fileName, printedCountBy, nextQueue];
          }
        }
      }
    };

    if (printedCountBy === 'all') {
      if (navigator.serviceWorker && location.protocol === 'https:') {
        this.useServiceWorker(frame, file, fileName);
      } else {
        frame.src = fileUrl;
        frame.onload = () => {
          this.setFilenameAndPrint(frame, fileName);
        };
      }
    } else {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.js';
      const loadingTask = pdfjsLib.getDocument({url: fileUrl, disableFontFace: false});
      loadingTask.promise.then(renderPages);
    }
  }

  private executeAction() {
    this.hidePrintingQueue();
    if (this.queuedAction !== undefined) {
      this.printPdf(this.queuedAction[0], null, this.queuedAction[1], this.queuedAction[2], this.queuedAction[3], this.queuedAction[4]);
    }
  }

  private setFilenameAndPrint(frame: HTMLIFrameElement, fileName = '', executeAction?: boolean) {
    if (!UtilService.getBrowser('ff')) {
      const pageTitle = cloneDeep(document.title);
      let pages = '';
      if (fileName) {
        if (this.queuedActionRef) {
          pages = ' (' + this.queuedActionRef.innerHTML.split(': ')[1].replace('/', '-') + ')';
        }
        document.title = fileName + pages;
      }
      setTimeout(() => {
        frame.contentWindow.print();
        document.title = pageTitle;
        if (executeAction) {
          this.executeAction();
        }
      }, UtilService.getBrowser('safari') ? 1000 : 500);
    }
  }

  private disposeIframe() {
    if (this.iFrameRef) {
      this.renderer.removeChild(document.body, this.iFrameRef);
      delete this.iFrameRef;
    }
  }

  private showPrintingQueue(continueFromPage: number, printedCountBy: string, numPages: number) {
    const body = document.getElementsByTagName('body')[0];
    this.queuedActionRef = this.renderer.createElement('div');
    this.renderer.addClass(this.queuedActionRef, 'printing-queue');
    if (UtilService.getBrowser('ff')) {
      this.renderer.addClass(this.queuedActionRef, 'ff');
    }
    let text = ' / ' + numPages;
    if (printedCountBy === 'all') {
      text = numPages + text;
    } else {
      const last = continueFromPage + parseInt(printedCountBy, 0) - 1;
      text = continueFromPage + ' - ' + (last > numPages ? numPages : last) + text;
    }
    this.queuedActionRef.innerHTML = this.translateService.instant('general.api_printing_pages') + ': ' + text;
    body.appendChild(this.queuedActionRef);
  }

  private hidePrintingQueue() {
    if (this.queuedActionRef) {
      this.renderer.removeChild(document.body, this.queuedActionRef);
      delete this.queuedActionRef;
    }
  }

  async useServiceWorker(frame: HTMLIFrameElement, file: Blob, fileName: string) {
    const reg_path = '/assets/js/name-forcer/';
    const url = reg_path + fileName + '.pdf';
    const store = await caches.open('name-forcer');

    await store.put(url, new Response(file));

    frame.src = url;
    frame.onload = (evt) => {
      this.setFilenameAndPrint(frame, fileName);
      store.delete(url);
    };
  }

  public isPeriodSameDayByPeriod(period: Period): boolean {
    return this.isPeriodSameDay(period?.start, period?.end);
  }
  public isPeriodSameDay(start: number | string, end: number | string): boolean {
    if (!isNaN(+start) && !isNaN(+end)) {
      start = +start * 1000;
      end = +end * 1000;
    }
    return moment(moment(end).startOf('day')).diff(moment(start).startOf('day'), 'days') === 0;
  }

  public getEventStatusBadge(statusId: number): string {
    switch (statusId) {
      case statuses.STATUS_PRESALE: return 'success';
      case statuses.STATUS_CANCELLED: return 'warning';
      default: return '';
    }
  }
  public getEventStateBadge(state: EventStateEnum): string {
    switch (state) {
      case EventStateEnum.Pending:
        return 'accent';
      case EventStateEnum.Declined:
      case EventStateEnum.SoldOut:
      case EventStateEnum.TicketsCurrentlyUnavailable:
      case EventStateEnum.PreSaleEnded:
        return 'warning'
      default:
        return '';
    }
  }

  public isEnterKey(event: KeyboardEvent): boolean {
    return event.key === UtilService.KEY_ENTER;
  }

  public mobileDownload(file: Blob, fileName: string) {
    const link = document.createElement('a');
    link.href = URL.createObjectURL(file);
    link.download = fileName;
    link.dispatchEvent(new MouseEvent('click'));
  }

  get isMobile(): boolean {
    return window.innerWidth <= 960;
  }
  get isScaledDown(): boolean {
    return window.innerWidth <= 1440;
  }

  static startLoader(name: string, exec: () => void): Loader {
    exec();
    return { name: name, started: moment().unix(), finished: null };
  }

  static finishLoader(exec: Loader) {
    exec.finished = moment().unix();
  }

  public isAllLoadersFinished(loaders: Record<string, Loader>) {
    return loaders && !!Object.values(loaders).filter((v: Loader) => !!v.finished).length;
  }

  public isPendingLoader(values: string | string[], loaders: Record<string, Loader>) {
    if (typeof values === 'string') {
      values = [values];
    }
    return !!values.filter((v) => (loaders && loaders[v] && !loaders[v].finished)).length;
  }

  public mobileFocus(eventTarget: EventTarget): void {
    if (this.isMobile) {
      this.scrollIntoView(eventTarget);
    }
  }
  public scrollIntoView(eventTarget: EventTarget): void {
    (eventTarget as HTMLElement).scrollIntoView({block: 'center', behavior: 'smooth'});
  }

  public getTimeStringFromDate(date: string): string {
    const time = moment(date);
    return time.get('h').toLocaleString('en-US', {minimumIntegerDigits: 2, useGrouping:false}) + ':' +
      time.get('m').toLocaleString('en-US', {minimumIntegerDigits: 2, useGrouping:false});
  }
  public getNumberFromTime(time: string): number {
    return Number(time.replace(':', ''));
  }

  public formatToIso(value: string, tz: string): string {
    if (value) {
      const plainDate = cloneDeep(value).split('+')[0].replace('T', ' ');
      const momentTz = moment.tz(plainDate, tz);
      return momentTz.toISOString();
    }
    return null;
  }

  public arrayMove(arr: any[], fromIndex: number, toIndex: number): any[] {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  }

  isValidIBAN(input: string): boolean {
    const CODE_LENGTHS: any = {
      AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
      CH: 21, CR: 22, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
      FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
      HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
      LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
      MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
      RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26,
      AL: 28, BY: 28, EG: 29, GE: 22, IQ: 23, LC: 32, SC: 31, ST: 25, SV: 28,
      TL: 23, UA: 29, VA: 22, VG: 24, XK: 20, MA: 28
    };
    const iban = String(input).toUpperCase().replace(/[^A-Z0-9]/g, '');
    const code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/);
    const specialChar = input.match(/\W|_/g);
    let digits;
    if (!code || specialChar || iban.length !== CODE_LENGTHS[code[1]]) {
      return false;
    }
    digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string): any => {
      return letter.charCodeAt(0) - 55;
    });
    return this.mod97(digits) === 1;
  }

  private mod97(value: string): number {
    let checksum: number = parseInt(value.slice(0, 2), 10);
    let fragment: string;
    for (let offset = 2; offset < value.length; offset += 7) {
      fragment = String(checksum) + value.substring(offset, offset + 7);
      checksum = parseInt(fragment, 10) % 97;
    }
    return checksum;
  }

  public get getGridSize(): number {
    return window.innerWidth > 1440 ? 12 : 9;
  }

  public compareForSort(a: string, b: string, sortOrder = SortOrderEnum.Asc): number {
    if (a > b) {
      return sortOrder === SortOrderEnum.Asc ? 1 : -1;
    } else if (a < b) {
      return sortOrder === SortOrderEnum.Asc ? -1 : 1;
    }
    return 0;
  }

  public downloadCSV(csv: string, fileName: string): void {
    if (csv) {
      const blobParts: any[] = [new Uint8Array([0xEF, 0xBB, 0xBF]), csv];
      const blob: Blob = new Blob(blobParts, { type: 'text/csv;charset=utf-8' });
      const anchorElement: HTMLAnchorElement = window.document.createElement('a');
      anchorElement.href = window.URL.createObjectURL(blob);
      anchorElement.download = fileName + '.csv';
      document.body.appendChild(anchorElement);
      anchorElement.click();
      document.body.removeChild(anchorElement);
    }
  }

  static getS3ImageFileName(imageId: string, ratio: string, thumb?: boolean) {
    return imageId + (ratio ? ('_' + ratio) : '') + (thumb ? '_thumb' : '') +  '.webp';
  }

  public formatDatesForFile(startDate: string, endDate: string, format: string): string {
    let formattedPeriod: string = '';
    if (startDate) {
      formattedPeriod = '_' + moment(startDate).format(format);
    }
    if (endDate) {
      formattedPeriod = formattedPeriod + '_' + moment(endDate).format(format);
    }
    return formattedPeriod;
  }
}

export interface Loader {
  name: string;
  started: number;
  finished: number;
}
